import React, { useRef, useState } from 'react';
import { Button, OutlineButton } from '../../components/display/button';
import { InsertionOrderSelect } from '../../features/strategy/insertion-order-select';
import { trpc } from '../../utils/trpc-client';
import { ZohoInsertionOrder } from 'shared/src/domain/zoho/zoho-types';
import { CSV_TYPE } from 'shared/src/utils/export/export-utils';
import { TooltipRefProps } from 'react-tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo, faXmark } from '@fortawesome/pro-light-svg-icons';
import { IODropdown } from '../../features/strategy/io-dropdown';
import { useOnClickOutside } from 'usehooks-ts';
import { FileExport } from '@/icons/file-export';
import { FileExported } from '@/icons/file-exported';
import { ExternalImport } from '@/icons/external-import';

type Props = {
  campaignId: string;
  strategyId: string;
  hasCustomPacing: boolean;
};

export function ExportIOButton({ campaignId, strategyId, hasCustomPacing }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<TooltipRefProps>(null);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [exported, setExported] = useState<boolean>(false);
  useOnClickOutside(containerRef as React.RefObject<HTMLElement>, () => setTooltipOpen(false));

  if (!exported) {
    return (
      <div ref={containerRef}>
        <OutlineButton
          data-tooltip-id="generate-io"
          selected={tooltipOpen}
          onClick={() => setTooltipOpen(!tooltipOpen)}>
          <FileExport color={'blue'} />
          <div>IO CSV</div>
        </OutlineButton>
        <IODropdown
          id="generate-io"
          afterHide={() => setExported(false)}
          isOpen={tooltipOpen}
          tooltipRef={tooltipRef}
          setIsOpen={setTooltipOpen}>
          <SelectIOTooltip
            onDownload={() => setExported(true)}
            campaignId={campaignId}
            strategyId={strategyId}
          />
        </IODropdown>
      </div>
    );
  }

  return (
    <div ref={containerRef}>
      <OutlineButton
        data-tooltip-id="generate-io"
        selected={tooltipOpen}
        onClick={() => setTooltipOpen(!tooltipOpen)}>
        <FileExported color={'blue'} />
        <div>Exported</div>
      </OutlineButton>
      <IODropdown
        id="generate-io"
        afterHide={() => setExported(false)}
        isOpen={tooltipOpen}
        tooltipRef={tooltipRef}
        setIsOpen={setTooltipOpen}>
        <WhatNextTooltip
          closeTooltip={() => tooltipRef.current?.close()}
          hasCustomPacing={hasCustomPacing}
        />
      </IODropdown>
    </div>
  );
}

type IOTooltipProps = {
  onDownload: () => void;
  campaignId: string;
  strategyId: string;
};

function SelectIOTooltip({ onDownload, campaignId, strategyId }: IOTooltipProps) {
  const { data, isLoading } = trpc.insertionOrders.useQuery({ zoho_campaign_id: campaignId });
  const [selectedIO, setSelectedIO] = useState<ZohoInsertionOrder | null>(null);

  const hasIOs = !!data && data.length > 0;

  return (
    <div className="mt-2 w-[350px] rounded-lg border border-gray-300 bg-white p-4">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-[16px] font-semibold text-gray-800">Generate IO export file</h2>
      </div>
      {isLoading ? (
        <div className="text-sm text-gray-700">Loading Campaign IO&#39;s...</div>
      ) : (
        <>
          {hasIOs ? (
            <InsertionOrderSelect selected={selectedIO} setSelected={setSelectedIO} ios={data} />
          ) : (
            <div className="flex items-center justify-center rounded-sm bg-gray-100 py-1 text-sm text-gray-800">
              No IOs found for this campaign
            </div>
          )}
          {selectedIO ? (
            <div className="mt-4 flex justify-end">
              <DownloadButton strategyId={strategyId} io={selectedIO} onDownload={onDownload} />
            </div>
          ) : (
            <CreateIOMessage hasIos={hasIOs} campaignId={campaignId} />
          )}
        </>
      )}
    </div>
  );
}

function WhatNextTooltip({
  closeTooltip,
  hasCustomPacing
}: {
  closeTooltip: () => void;
  hasCustomPacing: boolean;
}) {
  return (
    <div className="flex w-[333px] flex-col gap-4 rounded-lg bg-blue-50 px-4 py-4 text-gray-800">
      <div className="flex items-center justify-between font-semibold">
        What to do next...
        <div
          className="flex cursor-pointer rounded-sm bg-blue-100 px-[3px] py-px"
          onClick={closeTooltip}>
          <FontAwesomeIcon icon={faXmark} className="text-lg text-[#3b6fec]" />
        </div>
      </div>
      <div className="flex flex-col gap-4">
        <div className="flex items-start gap-2">
          <div className="flex items-start text-lg font-bold leading-none text-blue-700">1</div>
          <div className="text-sm">Upload the exported file in Zoho’s Insertion Order Module.</div>
        </div>
        <div className="flex items-start gap-2">
          <div className="flex items-start text-lg font-bold leading-none text-blue-700">2</div>
          <div className="text-sm">
            Select Import Flight Details using the arrow on the right side of the Create Insertion
            Order to import.
          </div>
        </div>
        {hasCustomPacing && (
          <>
            <div className="flex items-center rounded-sm bg-yellow-200">
              <FontAwesomeIcon className="px-1" icon={faCircleInfo} />
              <div className="w-full rounded-sm bg-yellow-100 p-1 text-sm font-semibold">
                Extra file for custom pacing flighting
              </div>
            </div>
            <div className="flex items-start gap-2">
              <div className="flex items-start text-lg font-bold leading-none text-blue-700">3</div>
              <div className="text-sm">
                To break out flights in Zoho according to the campaign’s custom pacing schedule,{' '}
                <span className="font-semibold">
                  you have a second file ending in pacing_schedule.
                </span>
              </div>
            </div>
          </>
        )}
      </div>
      <a
        href="https://crm.zoho.com/crm/org643932192/settings/import?module=CustomModule19&step=1"
        target="_blank"
        rel="noreferrer">
        <Button className="flex w-full justify-center" size="compact">
          <div className="flex items-center gap-2">
            <ExternalImport />
            Import Flight Details in Zoho
          </div>
        </Button>
      </a>
    </div>
  );
}

type ButtonProps = {
  strategyId: string;
  io: ZohoInsertionOrder | null;
  onDownload: () => void;
};

export function DownloadButton({ strategyId, io, onDownload }: ButtonProps) {
  const [loading, setLoading] = React.useState(false);

  async function handleDownload() {
    setLoading(true);
    try {
      const res = await fetch(`/api/v1/proposals/${strategyId}/export?ioId=${io?.id}`, {
        method: 'POST',
        body: JSON.stringify(io),
        credentials: 'include',
        headers: { Accept: CSV_TYPE, 'Content-Type': 'application/json' }
      });
      if (!res.ok) throw new Error(`Download failed (${res.status})`);

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;

      // extract filename from Content-Disposition or fallback
      const cd = res.headers.get('Content-Disposition') || '';
      const match = cd.match(/filename="?(.+?)"?($|;)/);
      a.download = match ? match[1] : `strategy-${strategyId}.xlsx`;

      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      onDownload();
    } catch (e: any) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  return (
    <Button
      data-tooltip-id="generate-io"
      onClick={handleDownload}
      disabled={io == null || loading}
      size="compact">
      {loading ? 'Exporting…' : 'Export IO'}
    </Button>
  );
}

function CreateIOMessage({ hasIos, campaignId }: { hasIos: boolean; campaignId: string }) {
  return (
    <div className="flex flex-col">
      <div className="my-4 h-px bg-gray-300" />
      <div className="mb-2 text-sm font-light text-gray-500">Haven&#39;t created the IO yet?</div>
      <a
        href={`https://crm.zoho.com/crm/org643932192/tab/Potentials/${campaignId}`}
        target="_blank"
        rel="noreferrer">
        {!hasIos ? (
          <Button className="flex justify-center" size="compact">
            Create Insertion Order in Zoho
          </Button>
        ) : (
          <div className="text-blue-600">Create Insertion Order in Zoho</div>
        )}
      </a>
    </div>
  );
}
